import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Form,
  Input,
  Modal,
  Row,
  Select,
  Statistic,
  Table,
  Tag,
  message,
} from 'antd';
import SuperAdminAPI from '../../../services/SuperAdminAPI';

const { Option } = Select;

const formatNumber = value => {
  const num = Number(value);
  if (!Number.isFinite(num)) return '0';
  return num.toLocaleString();
};

const CreditsWalletPanel = () => {
  const [wallets, setWallets] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [ledgerLoading, setLedgerLoading] = useState(false);
  const [ledger, setLedger] = useState([]);
  const [selectedWallet, setSelectedWallet] = useState(null);
  const [forecast, setForecast] = useState(null);
  const [forecastLoading, setForecastLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [actionType, setActionType] = useState('grant');
  const [form] = Form.useForm();

  const loadTenants = async () => {
    try {
      const result = await SuperAdminAPI.listTenants();
      setTenants(result || []);
    } catch (error) {
      console.error('CreditsWalletPanel: failed to load tenants', error);
      message.error('Failed to load tenants');
      setTenants([]);
    }
  };

  const loadWallets = async () => {
    setLoading(true);
    try {
      const result = await SuperAdminAPI.getCreditWallets();
      setWallets(result || []);
    } finally {
      setLoading(false);
    }
  };

  const loadLedger = async tenantId => {
    setLedgerLoading(true);
    setForecastLoading(true);
    try {
      const [walletResult, forecastResult] = await Promise.all([
        SuperAdminAPI.getCreditWallet(tenantId),
        SuperAdminAPI.getCreditForecast(tenantId),
      ]);
      setSelectedWallet(walletResult?.wallet || null);
      setLedger(walletResult?.ledger?.items || []);
      setForecast(forecastResult || null);
    } catch (error) {
      console.error('CreditsWalletPanel: failed to load wallet/forecast', error);
      message.error('Failed to load wallet details');
      setForecast(null);
      setLedger([]);
    } finally {
      setLedgerLoading(false);
      setForecastLoading(false);
    }
  };

  useEffect(() => {
    loadTenants();
    loadWallets();
  }, []);

  const tenantOptions = useMemo(
    () =>
      tenants.map(t => ({
        value: t._id,
        label: t.name || t.slug || t._id,
      })),
    [tenants]
  );

  const handleAction = (type, wallet) => {
    setActionType(type);
    form.setFieldsValue({
      tenantId: wallet?.tenantId?._id || wallet?.tenantId || undefined,
      amount: undefined,
      reason: type === 'grant' ? 'Admin credit grant' : 'Usage debit',
      idempotencyKey: undefined,
    });
    setModalOpen(true);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const payload = {
        tenantId: values.tenantId,
        amount: Number(values.amount),
        reason: values.reason,
        idempotencyKey: values.idempotencyKey || undefined,
      };
      if (actionType === 'grant') {
        await SuperAdminAPI.grantCredits(payload);
        message.success('Credits granted successfully');
      } else {
        await SuperAdminAPI.debitCredits(payload);
        message.success('Credits debited successfully');
      }
      setModalOpen(false);
      await loadWallets();
      if (values.tenantId) {
        await loadLedger(values.tenantId);
      }
    } catch (error) {
      const serverMessage = error?.response?.data?.message || error?.message || 'Credit action failed';
      message.error(serverMessage);
    }
  };

  const columns = [
    {
      title: 'Tenant',
      dataIndex: 'tenantId',
      key: 'tenant',
      render: tenant =>
        tenant ? (
          <span>
            {tenant.name || tenant.slug || tenant._id}
            <Tag style={{ marginLeft: 8 }}>{tenant.slug || 'tenant'}</Tag>
          </span>
        ) : (
          '—'
        ),
    },
    {
      title: 'Balance',
      dataIndex: 'balance',
      key: 'balance',
      render: value => formatNumber(value),
    },
    {
      title: 'Currency',
      dataIndex: 'currency',
      key: 'currency',
      render: value => value || 'KWD',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: value => <Tag color={value === 'active' ? 'green' : 'red'}>{value || 'active'}</Tag>,
    },
    {
      title: 'Updated',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      render: value => (value ? new Date(value).toLocaleString() : '—'),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, wallet) => (
        <span style={{ display: 'flex', gap: 8 }}>
          <Button size="small" onClick={() => loadLedger(wallet.tenantId?._id || wallet.tenantId)}>
            View
          </Button>
          <Button size="small" type="primary" onClick={() => handleAction('grant', wallet)}>
            Grant
          </Button>
          <Button size="small" danger onClick={() => handleAction('debit', wallet)}>
            Debit
          </Button>
        </span>
      ),
    },
  ];

  const ledgerColumns = [
    {
      title: 'Date',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: value => (value ? new Date(value).toLocaleString() : '—'),
    },
    { title: 'Delta', dataIndex: 'delta', key: 'delta' },
    { title: 'Balance', dataIndex: 'balanceAfter', key: 'balanceAfter' },
    { title: 'Reason', dataIndex: 'reason', key: 'reason' },
    { title: 'Source', dataIndex: 'source', key: 'source' },
  ];

  const daysRemaining = forecast?.forecast?.daysRemaining;
  const forecastStatus = forecast?.forecast?.status || 'no_burn';
  const forecastStatusText =
    forecastStatus === 'critical'
      ? 'Critical burn rate'
      : forecastStatus === 'at_risk'
        ? 'At risk'
        : forecastStatus === 'healthy'
          ? 'Healthy'
          : forecastStatus === 'depleted'
            ? 'Credits depleted'
            : 'No burn data yet';
  const burnRate = Number(forecast?.burnRate?.dailyBurnRate || 0);

  return (
    <Row gutter={[16, 16]}>
      <Col span={16}>
        <Card
          title="Credits Wallets"
          extra={
            <Button onClick={loadWallets} loading={loading}>
              Refresh
            </Button>
          }
        >
          <Table
            rowKey={record => record._id}
            loading={loading}
            columns={columns}
            dataSource={wallets}
            pagination={{ pageSize: 10 }}
          />
        </Card>
      </Col>
      <Col span={8}>
        <Card title="Ledger & Forecast">
          {selectedWallet ? (
            <Row gutter={[12, 12]} style={{ marginBottom: 12 }}>
              <Col span={12}>
                <Statistic
                  title="Balance"
                  value={Number(selectedWallet?.balance || 0)}
                  precision={2}
                  loading={forecastLoading}
                />
              </Col>
              <Col span={12}>
                <Statistic
                  title="Burn / Day"
                  value={burnRate}
                  precision={2}
                  loading={forecastLoading}
                />
              </Col>
              <Col span={24}>
                <Alert
                  type={
                    forecastStatus === 'critical' || forecastStatus === 'depleted'
                      ? 'error'
                      : forecastStatus === 'at_risk'
                        ? 'warning'
                        : 'success'
                  }
                  showIcon
                  message={forecastStatusText}
                  description={
                    daysRemaining === null || typeof daysRemaining === 'undefined'
                      ? 'Days remaining unavailable until enough usage data exists.'
                      : `${daysRemaining} day(s) of credits remaining at current burn rate.`
                  }
                />
              </Col>
            </Row>
          ) : null}
          <Table
            rowKey={record => record._id}
            loading={ledgerLoading || forecastLoading}
            columns={ledgerColumns}
            dataSource={ledger}
            pagination={{ pageSize: 5 }}
          />
        </Card>
      </Col>
      <Modal
        title={actionType === 'grant' ? 'Grant Credits' : 'Debit Credits'}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={handleSubmit}
        okText={actionType === 'grant' ? 'Grant' : 'Debit'}
      >
        <Form form={form} layout="vertical">
          <Form.Item name="tenantId" label="Tenant" rules={[{ required: true }]}>
            <Select placeholder="Select tenant">
              {tenantOptions.map(opt => (
                <Option key={opt.value} value={opt.value}>
                  {opt.label}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="amount" label="Amount" rules={[{ required: true }]}>
            <Input type="number" min={0} />
          </Form.Item>
          <Form.Item name="reason" label="Reason" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="idempotencyKey" label="Idempotency Key (optional)">
            <Input placeholder="Optional idempotency key" />
          </Form.Item>
        </Form>
      </Modal>
    </Row>
  );
};

export default CreditsWalletPanel;
